import React from 'react';
import { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { ExitToAppRounded, ChevronRight, Add } from '@material-ui/icons';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import Badge from '@material-ui/core/Badge';
import NotificationsIcon from '@material-ui/icons/Notifications';
import LanguageIcon from '@material-ui/icons/Language';
import ExpandMoreOutlined from '@material-ui/icons/ExpandMoreOutlined';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import ContactSupportOutlinedIcon from '@material-ui/icons/ContactSupportOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PrintIcon from '@material-ui/icons/Print';

import { appActions } from 'commons/reducer';
import { falsy, truthty } from 'utils/functions';
import { useIsMobileDevice } from 'utils/hooks/isMobileDevice';
import { useAutomation } from 'utils/hooks/useAutomationPermission';
import styles from './styles';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(styles);
const PDF_CONTINGENCY_MODE_KEY = 'pakit_pdf_contingency_mode';
const PRINTER_MODE_KEY = 'pakit_printer_mode';
const NETWORK_PRINTER_CONFIG_KEY = 'pakit_network_printer_config';

function NavBar(props) {
  const { t, i18n } = useTranslation();
  const { hasAutomation } = useAutomation();
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || i18n.language || 'en';
  });
  const [pdfContingencyMode, setPdfContingencyMode] = useState(
    localStorage.getItem(PDF_CONTINGENCY_MODE_KEY) === 'true',
  );
  const [printerDialogOpen, setPrinterDialogOpen] = useState(false);
  const {
    toggleDrawer,
    searchPrinter,
    device,
    deviceError,
    printerMode,
    networkPrinterConfig,
    networkPrinterStatus,
    setPrinterMode,
    setNetworkPrinterConfig,
    searchPrinterLoading,
    searchWeightScale,
    searchScaleLoading,
    scaleDevice,
    scaleDeviceError,
    withoutLogout,
    logOut,
    zendesk,
    queueData,
    clearNotifications,
    history,
    loadFromNotifications,
    user,
    role,
    clearBalanceNotifications,
    removeBalanceNotification,
  } = props;
  const [printerModeDraft, setPrinterModeDraft] = useState(printerMode || 'direct');
  const [networkPrinterDraft, setNetworkPrinterDraft] = useState({
    ip: networkPrinterConfig?.ip || '',
    port: networkPrinterConfig?.port || '9100',
  });

  useEffect(() => {
    if (i18n.language !== language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  useEffect(() => {
    setPdfContingencyMode(localStorage.getItem(PDF_CONTINGENCY_MODE_KEY) === 'true');
  }, [device, deviceError, searchPrinterLoading]);

  useEffect(() => {
    setPrinterModeDraft(printerMode || 'direct');
  }, [printerMode]);

  useEffect(() => {
    setNetworkPrinterDraft({
      ip: networkPrinterConfig?.ip || '',
      port: networkPrinterConfig?.port || '9100',
    });
  }, [networkPrinterConfig]);

  const getBaseLanguage = (lang) => {
    if (lang.startsWith('es') || ['cl', 'co', 'ec'].includes(lang)) {
      return 'es';
    }
    if (lang.startsWith('en') || lang === 'usa') {
      return 'en';
    }
    return lang;
  };
  const classes = useStyles();
  const dispatch = useDispatch();
  const countNotification = useSelector((state) => state.app.balanceNotificationCount);
  const balanceNotifications = useSelector((state) => state.app.balanceNotifications);
  const currentRole = useSelector((state) => state.app.role);
  const { notificationRoles } = useSelector((state) => state.app.account);
  const drawerIsOpen = useSelector((state) => state.app.drawerIsOpen);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);
  const [balanceAnchorEl, setBalanceAnchorEl] = React.useState(null);

  const isMobileDevice = useIsMobileDevice();

  const addAlertToList = async () => {
    try {
      const tenant = window.location.hostname.split('.')[0];

      const response = await fetch(
        `/api/v1/notifications/unresolved/${tenant}?offset=${countNotification}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const result = await response.json();

      if (result.success) {
        dispatch(appActions.addBalanceNotificacitonsToList(result.alerts));
      } else {
        console.error('Error obteniendo más alertas:', result.error);
      }
    } catch (error) {
      console.error('Error obteniendo más alertas:', error);
    }
  };

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
    setLanguage(event.target.value);
    localStorage.setItem('language', event.target.value);
  };

  const handlePdfContingencyToggle = (event) => {
    const enabled = event.target.checked;
    localStorage.setItem(PDF_CONTINGENCY_MODE_KEY, enabled ? 'true' : 'false');
    setPdfContingencyMode(enabled);
    dispatch(appActions.setSuccess(enabled ? t('pdf_mode_on') : t('pdf_mode_off')));
  };

  const persistPrinterSettings = (mode, config) => {
    localStorage.setItem(PRINTER_MODE_KEY, mode);
    localStorage.setItem(
      NETWORK_PRINTER_CONFIG_KEY,
      JSON.stringify({
        ip: config?.ip?.trim() || '',
        port: config?.port?.trim() || '9100',
      }),
    );
  };

  const handleOpenPrinterDialog = () => {
    setPrinterModeDraft(printerMode || 'direct');
    setNetworkPrinterDraft({
      ip: networkPrinterConfig?.ip || '',
      port: networkPrinterConfig?.port || '9100',
    });
    setPrinterDialogOpen(true);
  };

  const handleClosePrinterDialog = () => {
    setPrinterDialogOpen(false);
  };

  const handleSavePrinterSettings = () => {
    const normalizedConfig = {
      ip: networkPrinterDraft.ip.trim(),
      port: networkPrinterDraft.port.trim() || '9100',
    };

    persistPrinterSettings(printerModeDraft, normalizedConfig);
    setPrinterMode(printerModeDraft);
    setNetworkPrinterConfig(normalizedConfig);
    setPrinterDialogOpen(false);
    dispatch(appActions.setSuccess('Printer settings saved'));
  };

  const handleDisconnectPrinter = () => {
    const clearedConfig = {
      ip: '',
      port: '9100',
    };

    persistPrinterSettings('direct', clearedConfig);
    setPrinterModeDraft('direct');
    setNetworkPrinterDraft(clearedConfig);
    setPrinterMode('direct');
    setNetworkPrinterConfig(clearedConfig);
    setPrinterDialogOpen(false);
    dispatch(appActions.setSuccess('Printer disconnected'));
  };

  const handleTestPrinterConnection = () => {
    const normalizedConfig = {
      ip: networkPrinterDraft.ip.trim(),
      port: networkPrinterDraft.port.trim() || '9100',
    };

    persistPrinterSettings(printerModeDraft, normalizedConfig);
    setPrinterMode(printerModeDraft);
    setNetworkPrinterConfig(normalizedConfig);
    searchPrinter();
  };

  const getPrinterStatusText = () => {
    if (printerMode === 'network-ip') {
      if (networkPrinterStatus?.message) {
        return t(networkPrinterStatus.message);
      }

      if (networkPrinterConfig?.ip) {
        return `${networkPrinterConfig.ip}:${networkPrinterConfig.port || '9100'}`;
      }
    }

    if (device && truthty(device.productName)) {
      return device.productName;
    }

    return t(deviceError);
  };

  useEffect(() => {
    if (balanceNotifications.length === 0) {
      addAlertToList().then(() => {});
    }
  }, []);

  const handleBalanceClick = (event) => {
    if (balanceNotifications.length === 0) {
      addAlertToList();
    }
    setBalanceAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const trazabilityRedirect = () => {
    loadFromNotifications({ fromNoti: true });
    history.push(`/lotTraceability?lotIds[]=${queueData?.lotIds}`);
  };

  const handleMarkAsRead = async (specificAlertIds = null) => {
    try {
      const tenant = window.location.hostname.split('.')[0];
      const alertIds = specificAlertIds || balanceNotifications.map((alert) => alert.id);

      const response = await fetch(`/api/v1/notifications/read/${tenant}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ alertIds }),
      });
      const result = await response.json();
      if (result.success) {
        removeBalanceNotification(alertIds);
      } else {
        console.error('Error marcando como leída:', result.error);
      }
    } catch (error) {
      console.error('Error marcando alerta como leída:', error);
    }
  };

  const handleBalanceClose = () => {
    const unreadNotificationIds = balanceNotifications
      .filter((notification) => !notification.isRead)
      .map((notification) => notification.id);

    if (unreadNotificationIds.length > 0) {
      handleMarkAsRead(unreadNotificationIds);
    }
    setBalanceAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    history.push(`/process/${notification.processId}`);
    handleBalanceClose();
  };

  const handleZendesk = () => {
    zendesk.chasqikCreateMessenger();
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logOut();
  };

  const getUserInitials = () => {
    if (user?.full_name) {
      return user.full_name[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const getUserDisplayName = () => user?.full_name || user?.email || t('User');

  const getRoleDisplayName = () => role?.name || t('No role');

  const getAlertStyle = (type) => {
    switch (type) {
      case 'balance_error':
      case 'item_deletion':
        return {
          border: '1px solid #f44336',
          backgroundColor: '#ffebee',
          boxShadow: '0 2px 4px rgba(244, 67, 54, 0.2)',
        };

      case 'process_open_24h':
      case 'loss_exceeded':
        return {
          border: '1px solid #ff9800',
          backgroundColor: '#fff3e0',
          boxShadow: '0 2px 4px rgba(255, 152, 0, 0.2)',
        };
      case 'item_edit':
      case 'item_open':
      case 'item_close':
      case 'item_edit_process':
        return {
          border: '1px solid #2196f3',
          backgroundColor: '#e3f2fd',
          boxShadow: '0 2px 4px rgba(33, 150, 243, 0.2)',
        };
      default:
        return {
          border: '1px solid #4caf50',
          backgroundColor: '#e8f5e8',
          boxShadow: '0 2px 4px rgba(76, 175, 80, 0.2)',
        };
    }
  };

  const getChevorStyle = (type) => {
    switch (type) {
      case 'balance_error':
      case 'item_deletion':
        return {
          color: ' #f44336',
        };

      case 'process_open_24h':
      case 'loss_exceeded':
        return {
          color: '#ff9800',
        };
      case 'item_edit':
      case 'item_open':
      case 'item_close':
      case 'item_edit_process':
        return {
          color: '#2196f3',
        };
      default:
        return {
          color: ' #4caf50',
        };
    }
  };

  return (
    <span className="no-print">
      <AppBar position="static">
        <Toolbar>
          <IconButton
            onClick={toggleDrawer}
            edge="start"
            className={classes.menuButton}
            color="inherit"
            aria-label="menu">
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            Pakit
          </Typography>
          <div
            className={`${classes.controlsContainer} ${
              drawerIsOpen ? classes.controlsContainerShifted : ''
            }`}>
            {truthty(searchPrinter) && !isMobileDevice ? (
              <>
                <Tooltip title={t('Printer settings')}>
                  <IconButton
                    className={classes.deviceStatusButton}
                    onClick={handleOpenPrinterDialog}
                    aria-label="printer-settings">
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
            {truthty(searchPrinter) && !isMobileDevice ? (
              <Chip
                size="small"
                icon={
                  device && truthty(device.productName) ? (
                    <CheckCircleOutlineIcon />
                  ) : (
                    <ErrorOutlineIcon />
                  )
                }
                label={getPrinterStatusText()}
                clickable
                color="secondary"
                className={classes.printStatus}
                onDelete={searchPrinter}
                deleteIcon={searchPrinterLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              />
            ) : null}
            {!truthty(searchPrinter) || isMobileDevice ? (
              <Tooltip title={t('Printer settings')}>
                <IconButton
                  className={classes.deviceStatusButton}
                  onClick={handleOpenPrinterDialog}
                  aria-label="printer-settings">
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            {truthty(searchPrinter) && isMobileDevice ? (
              <Tooltip title={getPrinterStatusText()}>
                <IconButton
                  className={classes.deviceStatusButton}
                  onClick={searchPrinter}
                  aria-label="printer-status">
                  {searchPrinterLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : device && truthty(device.productName) ? (
                    <CheckCircleOutlineIcon style={{ color: '#81C784' }} />
                  ) : (
                    <ErrorOutlineIcon style={{ color: '#EF9A9A' }} />
                  )}
                </IconButton>
              </Tooltip>
            ) : null}
            {truthty(searchWeightScale) && !isMobileDevice ? (
              <Chip
                size="small"
                icon={
                  scaleDevice && truthty(scaleDevice.productName) ? (
                    <CheckCircleOutlineIcon />
                  ) : (
                    <ErrorOutlineIcon />
                  )
                }
                label={
                  scaleDevice && truthty(scaleDevice.productName)
                    ? scaleDevice.productName
                    : t(scaleDeviceError)
                }
                clickable
                color="secondary"
                className={classes.printStatus}
                onDelete={searchWeightScale}
                deleteIcon={searchScaleLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              />
            ) : null}
            {truthty(searchWeightScale) && isMobileDevice ? (
              <Tooltip
                title={
                  scaleDevice && truthty(scaleDevice.productName)
                    ? scaleDevice.productName
                    : t(scaleDeviceError)
                }>
                <IconButton
                  className={classes.deviceStatusButton}
                  onClick={searchWeightScale}
                  aria-label="scale-status">
                  {searchScaleLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : scaleDevice && truthty(scaleDevice.productName) ? (
                    <CheckCircleOutlineIcon style={{ color: '#81C784' }} />
                  ) : (
                    <ErrorOutlineIcon style={{ color: '#EF9A9A' }} />
                  )}
                </IconButton>
              </Tooltip>
            ) : null}
            {hasAutomation && (
              <Tooltip title={pdfContingencyMode ? t('pdf_mode_on') : t('pdf_mode_off')}>
                <FormControlLabel
                  className={classes.pdfContingencyToggle}
                  control={
                    <Switch
                      checked={pdfContingencyMode}
                      onChange={handlePdfContingencyToggle}
                      color="default"
                      size={isMobileDevice ? 'small' : 'medium'}
                    />
                  }
                  label={t('pdf_mode_label')}
                />
              </Tooltip>
            )}
            {notificationRoles?.includes(currentRole.name) ? (
              <IconButton onClick={handleBalanceClick}>
                <Badge
                  badgeContent={
                    (queueData?.lotIds ? 1 : 0) +
                    balanceNotifications.filter((notification) => !notification.isRead).length
                  }
                  classes={balanceNotifications.length > 0 ? { badge: classes.customBadge } : null}>
                  <NotificationsIcon fontSize="large" style={{ color: '#fff' }} />
                </Badge>
              </IconButton>
            ) : null}
            {queueData?.lotIds && queueData?.lotIds.length > 0 && (
              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                style={{ marginTop: 50 }}
                onClose={handleClose}>
                <MenuItem className={classes.menuItem} onClick={trazabilityRedirect}>
                  {t('Traceability load completed', { ids: queueData?.lotIds.join(', ') })}
                </MenuItem>
                <MenuItem className={classes.cleanMenuItem} onClick={clearNotifications}>
                  <Grid className={classes.cleanIconContainer}>
                    <DeleteIcon />
                    <span>{t('CLEAR')}</span>
                  </Grid>
                </MenuItem>
              </Menu>
            )}

            {balanceNotifications.length > 0 && (
              <Menu
                id="balance-menu"
                anchorEl={balanceAnchorEl}
                keepMounted
                open={Boolean(balanceAnchorEl)}
                onClose={handleBalanceClose}
                disableAutoFocusItem
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}>
                <div style={{ maxHeight: '25rem', overflow: 'auto', maxWidth: '400px' }}>
                  {balanceNotifications
                    .slice()
                    // eslint-disable-next-line no-nested-ternary
                    .sort((a, b) => (a.isRead === b.isRead ? 0 : a.isRead ? 1 : -1))
                    .map((notification) => (
                      <MenuItem key={notification.id} className={classes.menuItem}>
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                          <div
                            style={{
                              width: '10px',
                              height: '10px',
                              marginRight: '10px',
                              borderRadius: '100%',
                              background: !notification.isRead
                                ? 'rgb(229, 115, 115)'
                                : 'transparent',
                            }}
                          />
                          <div
                            onClick={
                              notification.type !== 'item_deletion'
                                ? () => handleNotificationClick(notification)
                                : undefined
                            }
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              padding: '12px',
                              borderRadius: '6px',
                              margin: '4px 0',
                              transition: 'all 0.2s ease',
                              flex: 1,
                              ...getAlertStyle(notification.type),
                            }}>
                            <div
                              style={{
                                flex: 1,
                                ...getChevorStyle(notification.type),
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere',
                              }}>
                              <strong>{notification.title}</strong>
                              <div>{notification.description}</div>
                              <small>{new Date(notification.timestamp).toLocaleString()}</small>
                            </div>
                            <div>
                              <ChevronRight
                                style={{ marginLeft: '10px', ...getChevorStyle(notification.type) }}
                              />
                            </div>
                          </div>
                        </div>
                      </MenuItem>
                    ))}
                </div>
                <div>
                  <MenuItem className={classes.menuItem}>
                    <Grid
                      onClick={() => addAlertToList()}
                      style={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        margin: '4px auto',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#00897b',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}>
                      <Add fontSize="small" style={{ marginRight: 4 }} />
                      <span>{t('See more')}</span>
                    </Grid>
                  </MenuItem>
                  <MenuItem className={classes.menuItem}>
                    <Grid
                      onClick={clearBalanceNotifications}
                      className={classes.cleanMenuItem}
                      style={{
                        border: '1px solid #888888',
                        borderRadius: '5px',
                        padding: '5px 50px 5px 50px',
                        background: '#00897b',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}>
                      <span>{t('CLEAR')}</span>
                    </Grid>
                  </MenuItem>
                </div>
              </Menu>
            )}
            {truthty(zendesk) && falsy(zendesk.isShowingZendeskButton) && (
              <Tooltip title={t('Contact us')}>
                <IconButton color="inherit" onClick={handleZendesk}>
                  <ContactSupportOutlinedIcon
                    fontSize="large"
                    style={{
                      width: 40,
                      height: 40,
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
            <div id="zendeskDiv" />

            <Box display="flex" alignItems="center" mr={0.5}>
              <LanguageIcon className={classes.languageIcon} />
              <Select
                onChange={(e) => handleLanguageChange(e)}
                value={getBaseLanguage(language)}
                IconComponent={ExpandMoreOutlined}
                className={classes.selectLanguage}
                disableUnderline>
                <MenuItem value="en">{t('English')}</MenuItem>
                <MenuItem value="es">{t('Spanish')}</MenuItem>
              </Select>
            </Box>
            {!withoutLogout && user && (
              <>
                <Box
                  display="flex"
                  alignItems="center"
                  onClick={handleUserMenuClick}
                  style={{
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      color: '#1976d2',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      marginRight: isMobileDevice ? '0' : '12px',
                      fontFamily: 'sans-serif',
                    }}>
                    {getUserInitials()}
                  </Box>

                  {!isMobileDevice && (
                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                      <Typography
                        variant="body2"
                        style={{
                          color: '#fff',
                          fontWeight: 'bold',
                          lineHeight: 1.2,
                        }}>
                        {getUserDisplayName()}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          color: 'rgba(255,255,255,0.8)',
                          lineHeight: 1.2,
                        }}>
                        {getRoleDisplayName()}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  PaperProps={{
                    style: {
                      minWidth: '250px',
                      marginTop: '30px',
                    },
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}>
                  <Box p={2} borderBottom="1px solid #e0e0e0">
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {getUserDisplayName()}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginBottom: '2px' }}>
                      {getRoleDisplayName()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {user?.email}
                    </Typography>
                  </Box>

                  <MenuItem onClick={handleLogout} style={{ padding: '12px 16px' }}>
                    <Box display="flex" alignItems="center">
                      <ExitToAppRounded style={{ marginRight: '12px', color: '#f44336' }} />
                      <Typography style={{ color: '#f44336', fontWeight: 'bold' }}>
                        {t('Log out')}
                      </Typography>
                    </Box>
                  </MenuItem>
                </Menu>
              </>
            )}
          </div>
        </Toolbar>
      </AppBar>
      <Dialog open={printerDialogOpen} onClose={handleClosePrinterDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('Printer settings')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginTop: 8, marginBottom: 16 }}>
            <InputLabel htmlFor="printer-mode-select">{t('Printer connection mode')}</InputLabel>
            <Select
              native
              value={printerModeDraft}
              onChange={(event) => setPrinterModeDraft(event.target.value)}
              inputProps={{ id: 'printer-mode-select' }}>
              <option value="direct">{t('Direct connection')}</option>
              <option value="network-ip">{t('Network connection')}</option>
            </Select>
          </FormControl>

          {printerModeDraft === 'network-ip' ? (
            <>
              <TextField
                fullWidth
                margin="dense"
                label={t('Printer IP')}
                value={networkPrinterDraft.ip}
                onChange={(event) => {
                  const { value } = event.target;
                  setNetworkPrinterDraft((prev) => ({
                    ...prev,
                    ip: value,
                  }));
                }}
              />
              <TextField
                fullWidth
                margin="dense"
                label={t('Printer port')}
                value={networkPrinterDraft.port}
                helperText={t('Default printer port helper')}
                onChange={(event) => {
                  const { value } = event.target;
                  setNetworkPrinterDraft((prev) => ({
                    ...prev,
                    port: value,
                  }));
                }}
              />
              {networkPrinterStatus?.message ? (
                <Box
                  mt={2}
                  style={{
                    color: networkPrinterStatus?.state === 'error' ? '#d32f2f' : '#424242',
                  }}>
                  {t(networkPrinterStatus.message)}
                </Box>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          {printerMode === 'network-ip' ? (
            <Button onClick={handleDisconnectPrinter} color="secondary">
              {t('Disconnect printer')}
            </Button>
          ) : null}
          <Button onClick={handleClosePrinterDialog}>{t('CANCEL')}</Button>
          {printerModeDraft === 'network-ip' ? (
            <Button onClick={handleTestPrinterConnection} color="primary">
              {t('Test printer connection')}
            </Button>
          ) : null}
          <Button onClick={handleSavePrinterSettings} color="primary" variant="contained">
            {t('Save printer settings')}
          </Button>
        </DialogActions>
      </Dialog>
    </span>
  );
}

export default NavBar;
